import React from 'react'
import { useTranslation } from 'react-i18next'

type SalesByMonthChartProps = {
    average: string
    legend: Array<{ code: string; label: string; color: string }>
    yAxisTicks: string[]
    months: Array<{
        label: string
        total: string
        segments: Array<{ color: string; heightPx: number; label: string }>
    }>
}

export const SalesByMonthChart: React.FC<SalesByMonthChartProps> = ({ average, legend, yAxisTicks, months }) => {
    const { t } = useTranslation('dashboard')
    return (
    <div className="bg-white rounded-2xl p-5 border border-neutralBlack-200 shadow-sm flex flex-col gap-[18px]">
        <div className="flex items-center gap-3.5 flex-wrap">
            <span className="text-[15px] font-semibold tracking-tight text-neutralBlack-900">{t('metrics.salesByMonthTitle')}</span>
            <span className="text-[13px] text-neutralBlack-600">{t('metrics.salesByMonthAverage', {n: average})}</span>
        </div>

        <div className="flex items-center gap-3.5 flex-wrap">
            {legend.map((item) => (
                <span key={item.code} className="inline-flex items-center gap-1.5 text-xs text-neutralBlack-800">
                    <span className="w-[9px] h-[9px] rounded-[3px]" style={{ background: item.color }} />
                    {item.label}
                </span>
            ))}
        </div>

        {months.length === 0 ? (
            <div className="h-[206px] flex items-center justify-center text-sm text-neutralBlack-500">
                {t('metrics.salesByMonthEmpty')}
            </div>
        ) : (
            <div className="flex gap-2.5" style={{ height: 206 }}>
                <div className="w-12 flex-none flex flex-col justify-between items-end" style={{ paddingBottom: 30 }}>
                    {yAxisTicks.map((t, i) => (
                        <span key={i} className="text-[10.5px] leading-none text-neutralBlack-400 whitespace-nowrap">{t}</span>
                    ))}
                </div>
                <div className="flex-1 min-w-0 flex items-end gap-2.5 relative">
                    <div className="absolute inset-x-0 top-0 flex flex-col justify-between pointer-events-none" style={{ bottom: 30 }}>
                        {yAxisTicks.map((_, i) => (
                            <span key={i} className="h-px bg-neutralBlack-100" />
                        ))}
                    </div>
                    {months.map((month) => (
                        <div key={month.label} className="relative z-10 flex-1 min-w-0 flex flex-col items-center gap-2">
                            <div title={month.total} className="flex-1 w-full flex flex-col justify-end items-center">
                                <div className="w-[26px] max-w-[70%] flex flex-col rounded-t overflow-hidden">
                                    {month.segments.map((segment, i) => (
                                        <span key={i} title={segment.label} style={{ background: segment.color, height: `${segment.heightPx}px` }} />
                                    ))}
                                </div>
                            </div>
                            <span className="text-[11px] text-neutralBlack-600">{month.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        )}
    </div>
    )
}
